import React, { useEffect, useState } from "react";
import { createRazorpayOrder, verifyRazorpayPayment } from "../api/walletApi";
import { loadRazorpayScript } from "../utils/loadRazorpay";

/**
 * Gates a premium action behind a Razorpay payment.
 *
 * Props:
 *  - title       short label of the thing being unlocked, e.g. "Kundli Milan Report"
 *  - price       amount in INR, fetched dynamically by the caller from the backend
 *  - onClose     called when the user dismisses the modal without paying
 *  - onSuccess   called once payment is verified server-side
 */

export default function PaymentModal({ title, price, onClose, onSuccess, createOrderFn }) {
  const [stage, setStage] = useState("confirm"); // confirm | processing | verifying | error
  const [error, setError] = useState(null);

  useEffect(() => {
    // Warm up the checkout script as soon as the modal opens.
    loadRazorpayScript().catch(() => {
      /* surfaced only when the user actually tries to pay */
    });
  }, []);

  const handlePay = async () => {
    setError(null);
    setStage("processing");
    try {
      const Razorpay = await loadRazorpayScript();
      const order = createOrderFn ? await createOrderFn() : await createRazorpayOrder(price);

      const rzp = new Razorpay({
        key: order.key_id,
        order_id: order.order_id,
        amount: Math.round(Number(order.amount) * 100),
        currency: order.currency || "INR",
        name: "Kundli Milan",
        description: title,
        prefill: order.user_phone ? { contact: order.user_phone } : undefined,
        theme: { color: "#D8622B" },
        handler: async (response) => {
          setStage("verifying");
          try {
            await verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            onSuccess();
          } catch (err) {
            setError(err.message || "We couldn't verify your payment. Please try again.");
            setStage("error");
          }
        },
        modal: {
          ondismiss: () => setStage("confirm"),
        },
      });

      rzp.on("payment.failed", (resp) => {
        setError(resp?.error?.description || "Payment failed. Please try again.");
        setStage("error");
      });

      rzp.open();
    } catch (err) {
      setError(err.message || "Something went wrong while starting the payment.");
      setStage("error");
    }
  };

  const busy = stage === "processing" || stage === "verifying";

  return (
    <div style={styles.overlay} onClick={busy ? undefined : onClose}>
      <div
        className="km-stars"
        style={styles.card}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Unlock ${title}`}
      >
        {!busy && (
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        )}

        <div style={styles.badge}>✦ Premium Report</div>
        <h2 style={styles.heading}>{title}</h2>
        <p style={styles.sub}>
          Unlock your complete, accurate reading — generated instantly after payment.
        </p>

        <div style={styles.priceRow}>
          <span style={styles.priceCurrency}>₹</span>
          <span style={styles.priceAmount}>{price}</span>
          <span style={styles.priceNote}>one-time</span>
        </div>

        {stage === "error" && <div style={styles.errorBox}>{error}</div>}

        <button
          style={{ ...styles.payBtn, ...(busy ? styles.payBtnDisabled : {}) }}
          onClick={handlePay}
          disabled={busy}
        >
          {stage === "processing" && "Opening secure checkout…"}
          {stage === "verifying" && "Verifying payment…"}
          {(stage === "confirm" || stage === "error") && `Pay ₹${price} & Continue`}
        </button>

        <div style={styles.secureRow}>
          <span>🔒 100% secure payments via Razorpay</span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(23, 15, 46, 0.55)",
    backdropFilter: "blur(3px)", display: "flex", alignItems: "center",
    justifyContent: "center", zIndex: 1000, padding: 16,
    animation: "km-pop 0.18s ease",
  },
  card: {
    position: "relative", width: "100%", maxWidth: 380,
    background: "linear-gradient(165deg, #241a47 0%, #170F2E 100%)",
    borderRadius: "var(--radius-lg)", padding: "32px 28px 26px",
    color: "#f4ead4", boxShadow: "var(--shadow-pop)",
    border: "1px solid rgba(232,169,74,0.25)",
    animation: "km-pop 0.22s var(--ease)",
    textAlign: "center",
  },
  closeBtn: {
    position: "absolute", top: 14, right: 16, background: "none", border: "none",
    color: "#c9bfe6", fontSize: 24, cursor: "pointer", lineHeight: 1,
  },
  badge: {
    display: "inline-block", fontFamily: "var(--font-body)", fontSize: 12,
    letterSpacing: 1.5, textTransform: "uppercase", color: "#E8A94A",
    border: "1px solid rgba(232,169,74,0.4)", borderRadius: 999,
    padding: "4px 12px", marginBottom: 14,
  },
  heading: {
    fontFamily: "var(--font-display)", fontSize: 24, margin: "0 0 8px",
    color: "#fbf5e9",
  },
  sub: { fontSize: 14, color: "#c9bfe6", margin: "0 0 20px", lineHeight: 1.5 },
  priceRow: {
    display: "flex", alignItems: "baseline", justifyContent: "center",
    gap: 4, marginBottom: 22,
  },
  priceCurrency: { fontSize: 22, color: "#E8A94A", fontWeight: 600 },
  priceAmount: { fontFamily: "var(--font-display)", fontSize: 44, color: "#fff", fontWeight: 700 },
  priceNote: { fontSize: 13, color: "#a89bd1", marginLeft: 6 },
  errorBox: {
    background: "rgba(179,64,47,0.18)", border: "1px solid rgba(179,64,47,0.5)",
    color: "#f3c9c1", borderRadius: 8, padding: "10px 12px", fontSize: 13,
    marginBottom: 16, textAlign: "left",
  },
  payBtn: {
    width: "100%", padding: "14px 18px", borderRadius: 999, border: "none",
    background: "linear-gradient(135deg, #E8A94A, #D8622B)", color: "#1c1109",
    fontWeight: 700, fontSize: 15, cursor: "pointer",
    boxShadow: "0 12px 24px -10px rgba(216,98,43,0.6)",
  },
  payBtnDisabled: { opacity: 0.75, cursor: "wait" },
  secureRow: { marginTop: 16, fontSize: 12, color: "#9d90c4" },
};
